import withRouter from "Components/Common/withRouter"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import React from "react"
import { ProjectSliceState, setProjectCount, setProjects, setQuery } from "slices/project-managment/projects/reducer"
import { IProjectQueryParam, ProjectListItem, ProjectUI } from "models/project"
import { downloadProjectsCsv, getMostRecentSyncLog, getPortfolioKpis, getProjects, getProjectSearchFields, syncProjects } from "slices/thunk"
import ProjectStatus from "models/project-status"
import Branch from "models/branch"
import Client from "models/client"
import Employee from "models/employee"
import { navigateToClientDetails, navigateToEmployeeDetails, navigateToProjectDetails } from "utils/navigator"
import DataTableTemplate from "Components/templates/data-table-template"
import SearchProjectsForm from "Components/organisms/forms/search-projects-form"
import ActionButton from "Components/atoms/buttons/action-button"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import { ProfileSliceState } from "slices/auth/profile/reducer"
import { SyncLogSliceState } from "slices/sync-log/reducer"
import { SyncLogTypes } from "models/sync-log"
import SyncButton from "Components/molecule/sync-button"
import { formatCurrency } from "utils/tools"
import useManual from "hooks/useManual"
import useSyncProcess from "hooks/useSyncProcess"
import { HelpIcon, DownloadIcon } from "Components/atoms/icons"
import HealthStatusChip from "Components/molecule/health-status-chip"
import PortfolioKpisRow from "Components/molecule/portfolio-kpis"
import { HealthStatus, PortfolioKpis, ProjectHealthUI } from "models/project-health"
import useProjectMarginTarget from "hooks/useProjectMarginTarget"
import { toDepartmentOptions } from "utils/department-options"

const ACTIVE_PROJECT_STATUS = 'Active'

const HEALTH_FILTER_OPTIONS = [HealthStatus.CRITICAL, HealthStatus.WARNING, HealthStatus.GOOD, HealthStatus.NOT_EVALUATED]
    .map(s => ({ value: s, label: ProjectHealthUI.label(s) }))

// Sort order for the Health column: worst first when descending
const HEALTH_SEVERITY: Record<HealthStatus, number> = {
    [HealthStatus.CRITICAL]: 3,
    [HealthStatus.WARNING]: 2,
    [HealthStatus.GOOD]: 1,
    [HealthStatus.NOT_EVALUATED]: 0,
}

const selectProjects = createSelector(
    (state: any) => state.Projects,
    ({
        projects,
        projectStatus,
        departments,
        branches,
        customers,
        projectManagers,
        projectCount,
        query,
    }: ProjectSliceState) => ({
        projects,
        projectStatus,
        departments,
        branches,
        customers,
        projectManagers,
        projectCount,
        query
    })
)

const selectSyncLog = createSelector(
    (state: any) => state.SyncLog,
    ({ syncLogs }: SyncLogSliceState) => ({
        syncLogs
    })
)

const ProjectListPage = (props: any) => {
    document.title = "Project | IbexIQ";
    const { handleOpenManual } = useManual("project-managment/projects");
    const [exporting, setExporting] = useState<boolean>(false)
    const [showSyncModal, setShowSyncModal] = useState<boolean>(false);
    const [isLoadingProjects, setIsLoadingProjects] = useState<boolean>(true)
    const [portfolioKpis, setPortfolioKpis] = useState<PortfolioKpis | null>(null)
    const [isLoadingKpis, setIsLoadingKpis] = useState<boolean>(true)
    const marginTarget = useProjectMarginTarget()

    const dispatch: any = useDispatch();
    const {
        projects,
        projectStatus,
        departments,
        branches,
        customers,
        projectManagers,
        projectCount,
        query
    } = useSelector(selectProjects)

    const {
        syncLogs
    } = useSelector(selectSyncLog)

    const queryProjects = useCallback(async (q: IProjectQueryParam) => {
        setIsLoadingProjects(true);
        const res = await getProjects(q);
        if (res) {
            dispatch(setProjects(res.data));
            dispatch(setProjectCount(res.count));
        }
        setIsLoadingProjects(false);
    }, [dispatch]);

    // Portfolio totals follow the filters, not the page
    const queryPortfolioKpis = useCallback(async (q: IProjectQueryParam) => {
        setIsLoadingKpis(true);
        setPortfolioKpis(await getPortfolioKpis(q));
        setIsLoadingKpis(false);
    }, []);

    const handleChangeQuery = (key: string, value: any) => {
        let q = { ...query };

        // All projects are loaded; sorting is done client-side
        q.page = 0;
        q.limit = Number.MAX_SAFE_INTEGER;
        q[key] = value

        dispatch(setQuery(q));
    };


    const handleExportToCSV = useCallback(async () => {
        setExporting(true);
        await downloadProjectsCsv(query);
        setExporting(false)
    }, [query])

    const initializePage = async () => {
        // if we dont have any of the search fields lets get them
        const q = { ...query };
        let s = projectStatus;

        if (
            projectStatus.length === 0 ||
            departments.length === 0
        ) {
            const res = await dispatch(getProjectSearchFields())
            s = res.projectStatus;
        }

        if (!syncLogs[SyncLogTypes.PROJECTS])
            await dispatch(getMostRecentSyncLog(SyncLogTypes.PROJECTS))

        const activeStatus = s.find((s: ProjectStatus) => s.description === ACTIVE_PROJECT_STATUS);
        if (activeStatus) q.statusIds = [activeStatus.id]
        dispatch(setQuery({ ...q }))

        queryPortfolioKpis(q)
        if (projects.length === 0) {
            await queryProjects(q)
        }

        setIsLoadingProjects(false)
    }

    // Load Projects + Search fields
    useEffect(() => {
        initializePage()
    }, [])

    const handleClickProject = (projectId: string) => {
        const url = `/project-managment/projects/project?projectId=${projectId}`
        window.open(url, "_blank", "noopener,noreferrer")
    }

    const handleClickProjectManager = (employeeId: string) => {
        navigateToEmployeeDetails(props.router.navigate, employeeId)
    }

    const handleClickCustomer = (customerId: string) => {
        navigateToClientDetails(props.router.navigate, customerId)
    }

    const columns = React.useMemo<ColumnDef<ProjectListItem>[]>(() => [
        {
            header: "Name",
            size: 115,
            accessorKey: "code",
            cell: ({ row }) => (
                <a
                    style={{
                        display: "block",
                        maxWidth: "100%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: "var(--bs-table-link-color)",
                        cursor: "pointer",
                    }}
                    onClick={() => handleClickProject(row.original.id)}
                >
                    {row.original.code || ""}
                </a>
            ),
        },
        {
            size: 150,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            header: "Description",
            accessorKey: "description",
        },
        {
            header: "Customer",
            id: "customer",
            size: 125,
            accessorFn: (row) => row.customer ?? "",
            cell: ({ row }) => {
                const customerId = row.original.customerId;
                const customerName = row.original.customer

                if (customerId) {
                    return <a
                        style={{
                            display: 'block',
                            border: "none",
                            padding: 0,
                            color: "var(--bs-table-link-color)",
                            cursor: "pointer",
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: "100%",
                        }}
                        onClick={() => handleClickCustomer(customerId)}
                    >
                        {customerName}
                    </a>
                } else {
                    return "Not Found"
                }
            }

        },
        {
            header: "Project Manager",
            id: "projectManager",
            size: 100,
            accessorFn: (row) => row.approver ?? "",
            meta: {
                getCellStyle: () => ({
                    background: "none",
                    color: "var(--bs-table-link-color)",
                    cursor: "pointer",
                })
            },
            cell: ({ row }) => {
                const projectManagerId = row.original.approverId;
                if (!projectManagerId) return "Not found";
                return <a
                    style={{
                        display: 'block',
                        border: "none",
                        padding: 0,
                        color: "var(--bs-table-link-color)",
                        cursor: "pointer",
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: "100%",
                    }}
                    onClick={() => handleClickProjectManager(projectManagerId)}
                >
                    {row.original.approver}
                </a>
            }
        },
        {
            header: "Branch",
            id: "branch",
            size: 75,
            accessorFn: (row) => row.branch ?? "",
            cell: ({ row }) =>
                row.original.branch ?? "Not Found",
        },
        {
            header: "Department",
            id: "department",
            size: 125,
            accessorFn: (row) => row.subAccount ?? "",
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: "100%",
                })
            },
            cell: ({ row }) =>
                row.original.subAccount ?? "Not Found",
        },
        {
            header: "Status",
            id: "status",
            size: 50,
            accessorFn: (row) => row.status ?? "",
            meta: {
                getHeaderClassName: () => 'text-end',
            },
            cell: ({ row }) => <strong className={ProjectUI.statusColor(row.original.status ?? undefined)}>{row.original.status ?? "N/A"}</strong>
        },
        // {
        //     header: "Revenue",
        //     id: "revenueActual",
        //     size: 90,
        //     meta: {
        //         getHeaderClassName: () => 'text-end',
        //         getCellClassName: () => 'text-end',
        //     },
        //     cell: ({ row }) => (
        //         <span className={ProjectUI.currencyValueColor(row.original.revenueActual)}>
        //             {formatCurrency(row.original.revenueActual)}
        //         </span>
        //     ),
        // },
        // {
        //     header: "Cost",
        //     id: "costActual",
        //     size: 90,
        //     meta: {
        //         getHeaderClassName: () => 'text-end',
        //         getCellClassName: () => 'text-end',
        //     },
        //     cell: ({ row }) => (
        //         <span className={ProjectUI.currencyValueColor(row.original.costActual)}>
        //             {formatCurrency(row.original.costActual)}
        //         </span>
        //     ),
        // },
        {
            header: "Health",
            id: "health",
            size: 75,
            accessorFn: (row) => row.healthStatus ? HEALTH_SEVERITY[row.healthStatus] : -1,
            cell: ({ row }) => (
                <HealthStatusChip
                    status={row.original.healthStatus}
                    failingChecks={row.original.failingChecks}
                    tooltipId={row.original.id}
                />
            ),
        },
        {
            header: "Margin %",
            id: "marginPercent",
            size: 75,
            accessorFn: (row) => Number(row.marginPercent),
            meta: {
                getHeaderClassName: () => 'text-end',
                getCellClassName: () => 'text-end',
            },
            cell: ({ row }) => (
                <strong className={ProjectUI.marginPercentColor(row.original.marginPercent, marginTarget)}>
                    {Number(row.original.marginPercent).toFixed(2)}%
                </strong>
            ),
        },
    ], [marginTarget]);

    const handleClearSearch = useCallback(() => {
        const q: IProjectQueryParam = {
            ...query,
            page: 0,
            limit: Number.MAX_SAFE_INTEGER,
            branchIds: [],
            clientIds: [],
            statusIds: [],
            code: '',
            projectManagerIds: [],
            departmentIds: [],
            healthStatuses: []
        }
        dispatch(setQuery(q));
    }, [setQuery, query]);


    const table = useDataTable({
        data: projects,
        columns,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    });

    const handleOpenSyncModal = () => setShowSyncModal(true)
    const handleCloseSyncModal = () => setShowSyncModal(false)


    const handleSearch = useCallback(async () => {
        queryPortfolioKpis(query);
        await queryProjects(query);
    }, [query, queryProjects, queryPortfolioKpis])

    const { isSyncing: isSyncingData, startSync } = useSyncProcess({
        module: SyncLogTypes.PROJECTS,
        start: syncProjects,
        onFinished: handleSearch,
    })

    const handleSyncProjects = async () => {
        handleCloseSyncModal();
        await startSync()
    }


    return <DataTableTemplate
        fullExport={handleExportToCSV}
        title={"Project Managment"}
        breadcrumbItem={"Projects"}
        table={table}
        columns={columns}
        isLoading={isLoadingProjects}
        beforeTable={<PortfolioKpisRow kpis={portfolioKpis} isLoading={isLoadingKpis} />}
        rowCount={projectCount}
        filters={
            <SearchProjectsForm
                handleClearSearch={handleClearSearch}
                query={query}
                handleChangeQuery={handleChangeQuery}
                branches={branches.map((b: Branch) => ({ value: b.id, label: b.name }))}
                projectManagers={projectManagers.map((emp: Employee) => ({ value: emp.id, label: `${emp.commonName ?? emp.firstName} ${emp.lastName}` }))}
                customers={customers.map((c: Client) => ({ value: c.id, label: c.name }))}
                status={projectStatus.map((pS: ProjectStatus) => ({ value: pS.id, label: pS.description }))}
                department={toDepartmentOptions(departments)}
                health={HEALTH_FILTER_OPTIONS}
                singleRow
                handleSearch={handleSearch}
                isSearching={isLoadingProjects}
                hide={undefined}
            />
        }
        actions={<>
            <SyncButton
                id="projectsSyncBtn"
                isSyncing={isSyncingData}
                syncLog={syncLogs[SyncLogTypes.PROJECTS]}
                onClick={handleOpenSyncModal}
            />
            {
                <ActionButton
                    onClick={handleOpenManual}
                >
                    Help <HelpIcon />
                </ActionButton>
            }
        </>}
        modal={< AreYouSureModal
            isOpen={showSyncModal}
            onModalClose={handleCloseSyncModal}
            handleAreYouSureDecline={async () => handleCloseSyncModal()}
            handleAreYouSureAccept={handleSyncProjects}
            modalTitle="Sync Projects"
            subTitle={"Sync acumatica and ibexIQ projects"}
            areYouSureacceptText="Sync"
            areYouSuredeclineText="Cancel"
        />}
    />
}


export default withRouter(ProjectListPage);